/**
 * Sincronización.
 *
 * Dos direcciones, con reglas distintas:
 *
 *   **Pull** (catálogo y mesas): el servidor manda, la copia local se
 *   sobrescribe. Es información de la que esta tablet no es dueña.
 *
 *   **Push** (outbox): las órdenes las creó esta tablet, así que se empujan
 *   en orden estricto de creación. Ese orden importa — agregar un ítem a una
 *   orden que el servidor todavía no conoce falla con 404. Por eso, si una
 *   operación de una orden falla, el resto de las operaciones **de esa misma
 *   orden** se dejan para la próxima vuelta, pero las de otras mesas siguen
 *   avanzando. Una mesa trabada no bloquea el restaurante entero.
 *
 * Backoff exponencial con techo de 5 minutos: reintentar cada segundo contra
 * un servidor caído solo calienta la batería de la tablet.
 */

import { db, guardarMeta, leerMeta, type OperacionOutbox } from './db';
import * as api from '../api/endpoints';
import { ErrorApi, ErrorRed } from '../api/cliente';
import type { RespuestaTurnoActual } from '../tipos/contrato';

const MAX_INTENTOS = 8;
const BASE_ESPERA_MS = 2_000;
const TECHO_ESPERA_MS = 5 * 60_000;

const esperaTras = (intentos: number) =>
  Math.min(BASE_ESPERA_MS * 2 ** intentos, TECHO_ESPERA_MS);

/* ─────────────────────────────── Pull ─────────────────────────────────── */

export async function bajarCatalogo(): Promise<void> {
  const catalogo = await api.traerCatalogo();

  const categorias = catalogo.categorias.map(({ productos: _p, ...c }) => c);
  const productos = [
    ...catalogo.categorias.flatMap((c) => c.productos),
    ...catalogo.sinCategoria,
  ];

  await db.transaction('rw', [db.categorias, db.productos], async () => {
    await db.categorias.clear();
    await db.productos.clear();
    await db.categorias.bulkPut(categorias);
    await db.productos.bulkPut(productos);
  });

  await guardarMeta('catalogoActualizadoEn', catalogo.actualizadoEn ?? new Date().toISOString());
}

/**
 * Baja el plano de mesas. Solo la definición —número, zona, capacidad—, no su
 * estado: el estado de la mesa lo deduce la UI de las órdenes locales, que
 * siempre están más frescas que lo que sepa el servidor.
 */
export async function bajarMesas(): Promise<void> {
  const { mesas } = await api.traerMesas();

  await db.transaction('rw', db.mesasRemotas, async () => {
    // Se borran solo los puestos que vinieron del servidor. Los creados en
    // esta tablet y todavía sin sincronizar —la barra que se abrió sin señal—
    // tienen que sobrevivir al pull: si se borraran, su cuenta quedaría
    // huérfana y desaparecería de la grilla con plata sin cobrar.
    const locales = await db.mesasRemotas.filter((m) => m.local).primaryKeys();
    const delServidor = await db.mesasRemotas.filter((m) => !m.local).primaryKeys();
    await db.mesasRemotas.bulkDelete(delServidor.filter((id) => !locales.includes(id)));

    await db.mesasRemotas.bulkPut(
      mesas.map((m) => ({
        id: m.id,
        numero: m.numero,
        nombre: m.nombre,
        zona: m.zona,
        capacidad: m.capacidad,
        esTemporal: m.esTemporal,
        estadoRemoto: m.estado,
        local: false,
      })),
    );
  });
}

/**
 * Estado de la caja. Se guarda en `meta` para que la app sepa, al arrancar sin
 * señal, si el turno de hoy ya está abierto. Sin esta copia, una tablet
 * offline no tendría forma de distinguir "la caja está abierta" de "todavía no
 * la abrieron", y ambas cosas exigen pantallas distintas.
 */
export async function bajarTurno(): Promise<RespuestaTurnoActual> {
  const actual = await api.turnoActual();
  await guardarMeta('turnoActual', actual);
  return actual;
}

export const turnoGuardado = () =>
  leerMeta<RespuestaTurnoActual | null>('turnoActual', null);

export async function bajarTodo(): Promise<void> {
  await Promise.all([bajarCatalogo(), bajarMesas(), bajarTurno()]);
}

/* ─────────────────────────────── Push ─────────────────────────────────── */

async function despachar(op: OperacionOutbox): Promise<void> {
  const c = op.carga;

  switch (op.tipo) {
    case 'orden.abrir': {
      const puestoNuevo = c['puestoNuevo'] as
        | { nombre?: string; zona?: string; capacidad?: number; esTemporal?: boolean }
        | undefined;

      const orden = await api.abrirOrden({
        id: String(c['id']),
        // O se manda el puesto existente, o se pide crear uno. Nunca ambos.
        ...(puestoNuevo
          ? { puestoNuevo }
          : { mesaId: (c['mesaId'] as string | null) ?? null }),
        ...(c['etiqueta'] ? { etiqueta: String(c['etiqueta']) } : {}),
        comensales: Number(c['comensales']),
        tipo: 'mesa',
        dispositivoId: String(c['dispositivoId']),
        origenOffline: Boolean(c['origenOffline']),
      });

      // El servidor manda el número definitivo de la orden; el local era
      // provisional.
      await db.ordenes.update(op.ordenId, {
        numero: orden.numero,
        estado: orden.estado,
        sincronizada: true,
        version: orden.version ?? 1,
        ...(orden.mesaId ? { mesaId: orden.mesaId, mesaNumero: orden.mesaNumeroSnap } : {}),
      });

      // Puesto creado al vuelo: el backend le dio su ID y su número real
      // (B1, B2…). Se reemplaza el provisional para que la grilla deje de
      // mostrar "B·" y ambas tablets vean el mismo puesto.
      const mesaLocalId = c['mesaLocalId'] as string | undefined;
      if (mesaLocalId && orden.mesaId && orden.mesaId !== mesaLocalId) {
        const provisional = await db.mesasRemotas.get(mesaLocalId);
        await db.transaction('rw', [db.mesasRemotas, db.ordenes], async () => {
          await db.mesasRemotas.delete(mesaLocalId);
          await db.mesasRemotas.put({
            id: orden.mesaId as string,
            numero: orden.mesaNumeroSnap ?? '··',
            nombre: provisional?.nombre ?? null,
            zona: provisional?.zona ?? null,
            capacidad: provisional?.capacidad ?? 2,
            esTemporal: provisional?.esTemporal ?? true,
            estadoRemoto: 'ocupada',
            local: false,
          });
          // Cualquier otra cuenta que se hubiera abierto en el puesto
          // provisional se reapunta al definitivo.
          await db.ordenes
            .where('mesaId')
            .equals(mesaLocalId)
            .modify({ mesaId: orden.mesaId, mesaNumero: orden.mesaNumeroSnap });
        });
      }
      return;
    }

    case 'orden.renombrar':
      await api.actualizarOrden(op.ordenId, {
        etiqueta: (c['etiqueta'] as string | null) ?? null,
        comensales: Number(c['comensales']),
      });
      return;

    case 'orden.mover':
      await api.moverCuenta(String(c['mesaDestinoId']), op.ordenId);
      return;

    case 'orden.item.agregar':
      await api.agregarItem(op.ordenId, {
        id: String(c['id']),
        productoId: String(c['productoId']),
        cantidad: Number(c['cantidad']),
      });
      return;

    case 'orden.item.actualizar':
      await api.actualizarItem(op.ordenId, String(c['itemId']), {
        cantidad: Number(c['cantidad']),
      });
      return;

    case 'orden.item.anular':
      await api.anularItem(op.ordenId, String(c['itemId']), String(c['motivo'] ?? ''));
      return;

    case 'orden.propina':
      await api.marcarPropina(op.ordenId, Boolean(c['aceptada']));
      return;

    case 'orden.cuenta':
      await api.pedirCuenta(op.ordenId);
      return;

    case 'orden.descartar':
      await api.descartarOrden(op.ordenId);
      return;

    case 'orden.pago': {
      const r = await api.registrarPago(op.ordenId, {
        id: String(c['id']),
        metodo: c['metodo'] as Parameters<typeof api.registrarPago>[1]['metodo'],
        ...(c['recibido'] !== undefined ? { recibido: Number(c['recibido']) } : {}),
        ...(c['referencia'] ? { referencia: String(c['referencia']) } : {}),
        ...(c['ultimosDigitos'] ? { ultimosDigitos: String(c['ultimosDigitos']) } : {}),
      });

      // `duplicado: true` significa que este cobro ya estaba registrado: el
      // ACK anterior se perdió, no que haya un problema. Se trata como éxito.
      await db.pagos.update(String(c['id']), { sincronizado: true });

      // Si el servidor calculó un total distinto al local —tarifa de propina
      // configurada diferente, por ejemplo— la cuenta queda con saldo y hay
      // que reabrirla para que el cajero cobre la diferencia.
      //
      // La comparación es contra `false` explícito, no `!r.completada`: si el
      // campo llegara ausente, `undefined` sería falsy y reabriríamos una
      // orden ya cobrada. Ese fue exactamente el síntoma de la mesa que
      // resucitaba con todos sus productos después de cerrarla.
      if (r.completada === false) {
        await db.ordenes.update(op.ordenId, { estado: 'cuenta_solicitada' });
      }
      return;
    }
  }
}

export interface ResultadoPush {
  enviadas: number;
  fallidas: number;
  pendientes: number;
}

export async function empujarOutbox(): Promise<ResultadoPush> {
  if (!navigator.onLine) {
    return { enviadas: 0, fallidas: 0, pendientes: await pendientes() };
  }

  const ahora = Date.now();
  const cola = await db.outbox.orderBy('id').toArray();

  // Si el `orden.abrir` de una cuenta quedó bloqueado, sus operaciones
  // dependientes tampoco pueden subir: para el servidor esa orden no existe y
  // cada intento devolvería 404. Sin esto, en la pasada siguiente
  // `ordenesTrabadas` arranca vacío y los ítems salen igual, ensuciando la
  // cola con errores que solo son eco del primero.
  const ordenesTrabadas = new Set<string>(
    cola.filter((o) => o.bloqueada && o.tipo === 'orden.abrir').map((o) => o.ordenId),
  );

  let enviadas = 0;
  let fallidas = 0;

  for (const op of cola) {
    if (op.bloqueada) continue;
    if (op.proximoIntentoEn > ahora) continue;
    if (ordenesTrabadas.has(op.ordenId)) continue; // preservar el orden causal

    try {
      await despachar(op);
      if (op.id !== undefined) await db.outbox.delete(op.id);
      enviadas++;
    } catch (e) {
      ordenesTrabadas.add(op.ordenId);
      fallidas++;

      const intentos = op.intentos + 1;
      const mensaje = e instanceof Error ? e.message : String(e);

      // Un 4xx no se arregla reintentando: el servidor rechazó el dato en sí.
      // Se agota rápido para que la operación quede visible como error real y
      // no se quede dando vueltas en la cola para siempre.
      const definitivo = e instanceof ErrorApi && e.esDefinitivo;
      const agotada = definitivo || intentos >= MAX_INTENTOS;

      if (op.id !== undefined) {
        await db.outbox.update(op.id, {
          intentos,
          ultimoError: mensaje,
          proximoIntentoEn: agotada ? Number.MAX_SAFE_INTEGER : Date.now() + esperaTras(intentos),
          bloqueada: agotada,
        });
      }

      // Sin red no tiene sentido seguir recorriendo la cola.
      if (e instanceof ErrorRed) break;
    }
  }

  return { enviadas, fallidas, pendientes: await pendientes() };
}

/** Solo las que todavía pueden subir; las bloqueadas ya no son "pendientes". */
export const pendientes = async () => {
  const todas = await db.outbox.toArray();
  return todas.filter((o) => !o.bloqueada).length;
};

/** Descarta las operaciones que el servidor rechazó de forma definitiva. */
export const descartarFallidas = async () => {
  const todas = await db.outbox.toArray();
  const ids = todas.filter((o) => o.bloqueada).map((o) => o.id!).filter(Boolean);
  await db.outbox.bulkDelete(ids);
  return ids.length;
};

/* ──────────────────────────── Ciclo de vida ───────────────────────────── */

let intervalo: number | undefined;

/**
 * Arranca el sincronizador. Se dispara al recuperar la conexión y cada 30 s,
 * que para un restaurante es de sobra: nada de lo que pasa acá necesita
 * latencia de milisegundos.
 */
export function iniciarSync(alCambiar?: (r: ResultadoPush) => void): () => void {
  const correr = () => {
    void empujarOutbox().then((r) => alCambiar?.(r));
  };

  correr();
  intervalo = window.setInterval(correr, 30_000);
  window.addEventListener('online', correr);

  return () => {
    if (intervalo) window.clearInterval(intervalo);
    window.removeEventListener('online', correr);
  };
}